// src/utils/reporteConsolidadoGenerator.js

// 👈 Importar función que obtiene los datos de la empresa actual
import { obtenerDatosEmpresa } from './tenantConfig';

// Configuración por defecto (fallback si no hay config del tenant)
const JEAN_INFORMATICO_CONFIG = {
  nombre: "JEAN INFORMATICO C.A.",
  rif: "J-405678901",
  direccion: "C.C. LATIN CENTER PB LOCAL 01",
  telefono: "0416-7594723",
  whatsapp: "04167594723",
  ciudad: "ACARIGUA - PORTUGUESA",
  email: "jean.informatico@gmail.com",
  sitioWeb: "www.jeaninformatico.com.ve",
  slogan: "Servicio Técnico Especializado"
};

/**
 * 👈 Obtiene la config de la empresa actual con fallback
 */
const getEmpresa = () => {
  try {
    const datos = obtenerDatosEmpresa();
    return {
      nombre: datos.nombreComercial || datos.nombre || JEAN_INFORMATICO_CONFIG.nombre,
      rif: datos.rif || JEAN_INFORMATICO_CONFIG.rif,
      direccion: datos.direccion || JEAN_INFORMATICO_CONFIG.direccion,
      telefono: datos.telefono1 || datos.telefono2 || JEAN_INFORMATICO_CONFIG.telefono,
      whatsapp: datos.whatsapp || datos.telefono1 || JEAN_INFORMATICO_CONFIG.whatsapp,
      ciudad: datos.ciudad || JEAN_INFORMATICO_CONFIG.ciudad,
      email: datos.email || JEAN_INFORMATICO_CONFIG.email,
      sitioWeb: datos.sitioWeb || JEAN_INFORMATICO_CONFIG.sitioWeb,
      slogan: datos.slogan || JEAN_INFORMATICO_CONFIG.slogan,
      logoBase64: datos.logoBase64 || null,
    };
  } catch (err) {
    console.warn('⚠️ No se pudo obtener config del tenant, usando fallback:', err);
    return { ...JEAN_INFORMATICO_CONFIG, logoBase64: null };
  }
};

/**
 * Formatear fecha para reporte
 */
const formatearFechaReporte = (fechaISO) => {
  if (!fechaISO) return 'No disponible';
  try {
    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString('es-VE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return fechaISO;
  }
};

/**
 * Formatear moneda USD
 */
const formatearMonedaUSD = (monto) => {
  if (monto === null || monto === undefined || isNaN(parseFloat(monto))) return '$0.00';
  return `$${parseFloat(monto).toFixed(2)}`;
};

/**
 * Generar HTML del Reporte Consolidado
 */
export const generarHTMLReporteConsolidado = (datos) => {
  const empresa = getEmpresa();
  const { ventas = [], servicios = [], fecha, stats = {} } = datos || {};
  const fechaReporte = formatearFechaReporte(new Date().toISOString());

  const totalVentas = stats.ventas || 0;
  const totalServicios = stats.servicios || 0;
  const ingresoTotal = totalVentas + totalServicios;

  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <title>Reporte Consolidado Diario - ${empresa.nombre}</title>
      <style>
        body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 30px; color: #333; line-height: 1.6; }
        .header { display: flex; justify-content: space-between; border-bottom: 3px solid #1e40af; padding-bottom: 15px; margin-bottom: 25px; }
        .logo-area { display: flex; align-items: center; gap: 15px; }
        .logo-img { height: 60px; max-width: 200px; object-fit: contain; }
        .empresa-info h1 { margin: 0; color: #1e40af; font-size: 24px; }
        .rif { font-size: 12px; color: #666; }
        
        .titulo-reporte { text-align: center; margin-bottom: 30px; }
        .titulo-reporte h2 { text-transform: uppercase; letter-spacing: 2px; color: #1e293b; margin-bottom: 5px; }
        
        .resumen-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-bottom: 40px; }
        .card { padding: 20px; border-radius: 12px; color: white; text-align: center; box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
        .card-blue { background: linear-gradient(135deg, #3b82f6, #1e40af); }
        .card-purple { background: linear-gradient(135deg, #a855f7, #6b21a8); }
        .card-indigo { background: linear-gradient(135deg, #6366f1, #3730a3); }
        .card h3 { margin: 0; font-size: 14px; opacity: 0.9; text-transform: uppercase; }
        .card p { margin: 10px 0 0; font-size: 28px; font-weight: 900; }

        .seccion { margin-bottom: 40px; }
        .seccion-header { background: #f8fafc; padding: 10px 15px; border-left: 5px solid #1e40af; margin-bottom: 15px; display: flex; justify-content: space-between; align-items: center; }
        .seccion-header h3 { margin: 0; color: #1e40af; }

        table { width: 100%; border-collapse: collapse; font-size: 11px; margin-bottom: 10px; }
        th { background: #f1f5f9; text-align: left; padding: 10px; border-bottom: 2px solid #cbd5e1; font-weight: bold; color: #475569; }
        td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
        .text-right { text-align: right; }
        .font-bold { font-weight: bold; }
        
        .footer { margin-top: 50px; text-align: center; font-size: 10px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 20px; }
        
        @media print {
          .no-print { display: none; }
          body { padding: 0; }
          .card { box-shadow: none; border: 1px solid #ddd; color: #333 !important; background: none !important; }
          .card h3 { color: #666; }
          .card p { color: #000; }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="logo-area">
          ${empresa.logoBase64 ? `<img src="${empresa.logoBase64}" class="logo-img" onerror="this.style.display='none'">` : ''}
          <div class="empresa-info">
            <h1>${empresa.nombre}</h1>
            <div class="rif">RIF: ${empresa.rif} | ${empresa.direccion}</div>
          </div>
        </div>
      </div>

      <div class="titulo-reporte">
        <h2>Reporte General de Operaciones</h2>
        <p>Resumen de ingresos correspondientes al día ${fecha || fechaReporte}</p>
        <p style="font-size: 11px; color: #94a3b8;">Generado: ${fechaReporte}</p>
      </div>

      <div class="resumen-cards">
        <div class="card card-blue">
          <h3>Ventas de Hoy</h3>
          <p>${formatearMonedaUSD(totalVentas)}</p>
        </div>
        <div class="card card-purple">
          <h3>Servicios de Hoy</h3>
          <p>${formatearMonedaUSD(totalServicios)}</p>
        </div>
        <div class="card card-indigo">
          <h3>Ingreso Total</h3>
          <p>${formatearMonedaUSD(ingresoTotal)}</p>
        </div>
      </div>

      <!-- SECCIÓN VENTAS -->
      <div class="seccion">
        <div class="seccion-header">
          <h3>Detalle de Ventas</h3>
          <span>Total Ventas: ${ventas.length}</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Factura / Nota</th>
              <th>Cliente</th>
              <th>Tipo</th>
              <th class="text-right">Monto Total $</th>
              <th class="text-right">Cobrado $</th>
            </tr>
          </thead>
          <tbody>
            ${ventas.map(v => `
              <tr>
                <td>${v.numeroFactura || v.numero_factura || v.NumeroFactura || 'N/A'}</td>
                <td>${v.clienteNombre || v.ClienteNombre || v.nombreCliente || v.NombreCliente || (v.cliente && (v.cliente.nombre || v.cliente.Nombre)) || (v.Cliente && (v.Cliente.Nombre || v.Cliente.nombre)) || 'S/D'}</td>
                <td>${v.tipoVenta || v.tipo_venta || v.TipoVenta || 'CONTADO'}</td>
                <td class="text-right">${formatearMonedaUSD(v.totalUsd || v.total_usd)}</td>
                <td class="text-right font-bold">${formatearMonedaUSD(v.cobradoHoy || 0)}</td>
              </tr>
            `).join('')}
            ${ventas.length === 0 ? '<tr><td colspan="5" style="text-align:center; padding: 20px;">No hubo ventas hoy</td></tr>' : ''}
          </tbody>
        </table>
      </div>

      <!-- SECCIÓN SERVICIOS -->
      <div class="seccion">
        <div class="seccion-header">
          <h3>Detalle de Servicios</h3>
          <span>Total Servicios: ${servicios.length}</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Orden #</th>
              <th>Cliente</th>
              <th>Equipo / Servicio</th>
              <th class="text-right">Total Orden $</th>
              <th class="text-right">Cobrado $</th>
            </tr>
          </thead>
          <tbody>
            ${servicios.map(s => `
              <tr>
                <td>#${s.numeroOrden || s.numero_orden || s.NumeroOrden || 'N/A'}</td>
                <td>${s.nombreCliente || s.NombreCliente || s.clienteNombre || s.ClienteNombre || (s.cliente && (s.cliente.nombre || s.cliente.Nombre)) || (s.Cliente && (s.Cliente.Nombre || s.Cliente.nombre)) || 'S/D'}</td>
                <td>${s.descripcion || s.Descripcion || 'S/D'}</td>
                <td class="text-right">${formatearMonedaUSD(s.precioTotalUsd || s.precio_total_usd)}</td>
                <td class="text-right font-bold">${formatearMonedaUSD(s.montoCobradoUsd || 0)}</td>
              </tr>
            `).join('')}
            ${servicios.length === 0 ? '<tr><td colspan="5" style="text-align:center; padding: 20px;">No hubo servicios hoy</td></tr>' : ''}
          </tbody>
        </table>
      </div>

      <div class="footer">
        Este documento es un resumen administrativo de caja.<br>
        ${empresa.nombre} | ${empresa.sitioWeb} | ${empresa.email}<br>
        © ${new Date().getFullYear()} - Todos los derechos reservados
      </div>

      <div class="no-print" style="margin-top: 30px; text-align: center;">
        <button onclick="window.print()" style="padding: 10px 20px; cursor: pointer; background: #1e40af; color: white; border: none; border-radius: 5px;">
          🖨️ Imprimir este Reporte
        </button>
        <button onclick="window.close()" style="padding: 10px 20px; cursor: pointer; background: #666; color: white; border: none; border-radius: 5px; margin-left: 10px;">
          ✕ Cerrar
        </button>
      </div>
    </body>
    </html>
  `;
};

/**
 * Imprimir reporte consolidado
 */
export const imprimirReporteConsolidado = async (datos) => {
  try {
    const html = generarHTMLReporteConsolidado(datos);
    const win = window.open('', '_blank', 'width=1200,height=800');
    if (!win) {
      alert('Por favor permite ventanas emergentes para imprimir el reporte');
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
  } catch (error) {
    console.error("Error al imprimir reporte consolidado:", error);
    alert('Error al generar el reporte');
  }
};

export default {
  generarHTMLReporteConsolidado,
  imprimirReporteConsolidado
};